export enum AmplifyStatusCode {
  NotAuthorizedException = 'NotAuthorizedException',
  CodeMismatchException = 'CodeMismatchException',
  LimitExceededException = 'LimitExceededException',
  UserNotFoundException = 'UserNotFoundException',
  InvalidParameterException = 'InvalidParameterException',
  TooManyRequestsException = 'TooManyRequestsException',
  ExpiredCodeException = 'ExpiredCodeException',
  TooManyFailedAttemptsException = 'TooManyFailedAttemptsException',
  InvalidPasswordException = 'InvalidPasswordException',
  UserNotConfirmedException = 'UserNotConfirmedException',
  PasswordResetRequiredException = 'PasswordResetRequiredException',
}

export const handleSignInError = (err: any,
  captureException: (error: any, data?: any) => void,
  email: string) => {
  // Not passing "err" as the first argument to captureException because
  // it doesn't seem to know it's an "Error"
  captureException(`Error signIn ${err.code}`, { source: 'signIn', err, email });

  // https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_InitiateAuth.html#API_InitiateAuth_Errors
  switch (err.code) {
    case AmplifyStatusCode.NotAuthorizedException:
    case AmplifyStatusCode.InvalidParameterException: {
      throw new Error('Email or password is incorrect.');
    }
    case AmplifyStatusCode.UserNotFoundException:
      throw new Error('Email was not found.');
    case AmplifyStatusCode.TooManyRequestsException:
      throw new Error('Login attempt limit exceeded. Please try again later or contact customer support.');
    case AmplifyStatusCode.UserNotConfirmedException:
      throw new Error('Email not verified. Please verify your account email or contact customer support.');
    case AmplifyStatusCode.PasswordResetRequiredException:
      throw new Error('Password reset is required. Please press Forgot Password to reset your password or contact customer support.');
    default:
      // InternalError
      // InvalidLambdaResponseException
      // InvalidUserPoolConfigurationException
      // ResourceNotFoundException
      // UnexpectedLambdaException
      // UserLambdaValidationExceptions
      // InvalidSmsRoleAccessPolicyException
      // InvalidSmsRoleTrustRelationshipException
      throw new Error('SignIn Error. Please check your internet connection and try again or contact customer support.');
  }
};

export const handleForgotPasswordError = (err: any,
  captureException: (error: any, data?: any) => void,
  email: string,
  callback: (errorMessage: { title: string, subtitle: string }) => void) => {
  // Not passing "err" as the first argument to captureException because
  // it doesn't seem to know it's an "Error"
  captureException(`Error sendForgotPasswordTo ${err.code}`, { source: 'sendForgotPasswordCodeTo', err, email });

  // https:// docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ForgotPassword.html#API_ForgotPassword_Errors
  switch (err.code) {
    case AmplifyStatusCode.LimitExceededException:
    case AmplifyStatusCode.TooManyRequestsException: {
      callback({
        title: 'Reset password attempt limit exceeded.',
        subtitle: 'Please try again later or contact customer support.',
      });
      break;
    }
    case AmplifyStatusCode.UserNotFoundException:
    case AmplifyStatusCode.InvalidParameterException: {
      callback({
        title: 'Email is not associated to an account in our system.',
        subtitle: 'Please try again with a valid email.',
      });
      break;
    }
    default:
      // CodeDeliveryFailureException
      // InternalErrorException
      // InvalidEmailRoleAccessPolicyException
      // InvalidLambdaResponseException
      // InvalidSmsRoleAccessPolicyException
      // InvalidSmsRoleTrustRelationshipException
      // ResourceNotFoundException
      // UnexpectedLambdaException
      // UserLambdaValidationException
      callback({
        title: 'There was an error sending you a reset password email.',
        subtitle: 'Please check your internet connection and try again or contact customer support.',
      });
      break;
  }
};

export const handleSetNewPasswordError = (err: any,
  captureException: (error: any, data?: any) => void,
  email: string,
  code: string,
  callback: (errorMessage: { title: string, subtitle: string }) => void) => {
  // Not passing "err" as the first argument to captureException because
  // it doesn't seem to know it's an "Error"
  captureException(`Error sendNewPasswordRequest ${err.code}`, {
    source: 'sendNewPasswordRequest', err, email, code,
  });
  // https:// docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ForgotPassword.html#API_ForgotPassword_Errors
  switch (err.code) {
    case AmplifyStatusCode.LimitExceededException:
    case AmplifyStatusCode.TooManyFailedAttemptsException:
    case AmplifyStatusCode.TooManyRequestsException: {
      callback({
        title: 'Set new password attempt limit exceeded.',
        subtitle: 'Please try again later or contact customer support.',
      });
      break;
    }
    case AmplifyStatusCode.ExpiredCodeException: {
      callback({
        title: 'Verification code has been expired',
        subtitle: 'Please try to reset password again.',
      });
      break;
    }
    case AmplifyStatusCode.CodeMismatchException:
      callback({
        title: 'The verification code provided did not match.',
        subtitle: 'Please try again.',
      });
      break;
    case AmplifyStatusCode.InvalidPasswordException:
      callback({
        title: 'Invalid password input.',
        subtitle: 'Please try again with a new password.',
      });
      break;
    case AmplifyStatusCode.InvalidParameterException:
      callback({
        title: 'Invalid input detected.',
        subtitle: 'Please try again.',
      });
      break;
    default:
      // NotAuthorizedException
      // UserNotConfirmedException
      // UserNotFoundException
      // InternalErrorException
      // InvalidLambdaResponseException
      // UnexpectedLambdaException
      // UserLambdaValidationException
      // ResourceNotFoundException
      callback({
        title: 'There was an error setting a new password.',
        subtitle: 'Please check your internet connection and try again or contact customer support.',
      });
      break;
  }
};

export const handleSignInWithNewPasswordError = (err: any,
  captureException: (error: any, data?: any) => void,
  signedInUser: any,
  callback: (errorMessage: { title: string, subtitle: string }) => void) => {
  // Not passing "err" as the first argument to captureException because
  // it doesn't seem to know it's an "Error"
  captureException(`Error signInWithNewPassword ${err.code}`, { source: 'signInWithNewPassword', err, signedInUser });

  // https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ChangePassword.html#API_ChangePassword_Errors
  // https:// docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_GetUser.html#API_GetUser_Errors
  switch (err.code) {
    case AmplifyStatusCode.LimitExceededException:
    case AmplifyStatusCode.TooManyRequestsException: {
      callback({
        title: 'Set password limit exceeded.',
        subtitle: 'Please try again later or contact customer support.',
      });
      break;
    }
    case AmplifyStatusCode.InvalidParameterException:
    case AmplifyStatusCode.InvalidPasswordException: {
      callback({
        title: 'Invalid password input.',
        subtitle: 'Please try again with a new password.',
      });
      break;
    }
    case AmplifyStatusCode.PasswordResetRequiredException: {
      callback({
        title: 'Password reset is required.',
        subtitle: 'Please press Forgot Password to reset your password or contact customer support.',
      });
      break;
    }
    default:
      // NotAuthorizedException
      // ResourceNotFoundException
      // InternalErrorException
      // UserNotConfirmedException
      // UserNotFoundException
      callback({
        title: 'There was an error setting your new password.',
        subtitle: 'Please check your internet connection and try again or contact customer support for help.',
      });
      break;
  }
};
